import { memo, type ElementType } from "react";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  Box,
  ButtonBase,
  Chip,
  Divider,
  Fab,
  Stack,
  Typography,
  keyframes,
  styled,
} from "@mui/material";

import { NO_DISPLAY_IMAGE } from "@/constants/images";
import { imageVariantUrl, VARIANT } from "@/utils/imageVariant";
import { FONT_WEIGHT_BOLD, PAGE_PADDING, SHAPE } from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";
import type { MenuItem } from "@/types";
import ItemPrice from "./ItemPrice";

/**
 * The three per-category item grids.
 *
 * `category.display_type` is a **per-category** setting — `REGULAR` (the
 * default), `CLASSIC` or `SIMPLE` — and it selects a different grid AND a
 * different tile, not a variant of one tile. This reconstruction rendered a
 * single hand-measured tile for every category and ignored the field entirely,
 * which is why the menu never matched: this tenant's own categories are 3
 * REGULAR, 3 CLASSIC and 2 SIMPLE.
 *
 * The names are counter-intuitive and worth stating: **CLASSIC is the wide
 * horizontal list row** with the image on the right, and **SIMPLE is the
 * richest card** — 20px radius, a divider and an "Add To Cart" chip.
 *
 * `isModernDesign` (from `settings.web_design_type` starting with `MODERN`)
 * layers on top and only ever changes column counts and column gaps.
 */

/* ------------------------------- shared bits ------------------------------- */

/** The "Popular" bounce: −8px then −4px. REGULAR and CLASSIC share it. */
const popIn = keyframes({
  "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
  "40%": { transform: "translateY(-8px)" },
  "60%": { transform: "translateY(-4px)" },
});

/** SIMPLE's is shallower: −3px then −1px. */
const popInSmall = keyframes({
  "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
  "40%": { transform: "translateY(-3px)" },
  "60%": { transform: "translateY(-1px)" },
});

const BOTTOM_GRADIENT =
  "linear-gradient(to top, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.1) 30%, transparent 50%)";

/**
 * `eR` / `em` / `e$` — the quick-add Fab. Identical in all three tiles.
 *
 * `component="span"` is load-bearing: two of the three tiles are themselves a
 * `ButtonBase`, and a nested `<button>` is invalid HTML.
 */
const QuickAdd = styled(Fab)<{ component?: ElementType }>({
  position: "absolute",
  zIndex: Z_INDEX.NONE,
  padding: "4px",
  top: 7,
  right: 8,
  fontSize: "1.1rem",
});

const clamp = (lines: number) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical" as const,
});

export interface ItemTileProps {
  item: MenuItem;
  itemQuantity?: number;
  isModernDesign?: boolean;
  hidePrice?: boolean;
  onClick: (item: MenuItem) => void;
  symbol?: string;
  code?: string;
}

/**
 * The tile's image source — a 400 px WEBP VARIANT of the item photo rather
 * than the original.
 *
 * The source photographs are 5168x3448 and larger, which a browser has to
 * decode into a 68 MB+ RGBA surface before it can paint a tile five to a row.
 * `imageVariantUrl` returns the original untouched for anything it cannot
 * resize, and `onTileImageError` below restores the original if the variant
 * request itself fails, so nothing here can leave a tile blank.
 */
const imageOf = (item: MenuItem) => imageVariantUrl(item.image_url || "", VARIANT.TILE);

/** Begin fetching/decoding the detail image on pointer-down, which precedes a
 * click by enough time to remove the measured decode task from modal paint. */
const preloadDetailImage = (item: MenuItem) => {
  if (typeof window === "undefined") return;
  const source = imageVariantUrl(item.image_url || "", VARIANT.DETAIL);
  if (!source) return;
  const image = new Image();
  image.src = source;
  void image.decode?.().catch(() => undefined);
};

/**
 * Tile `onError`, in two steps: first put the real ORIGINAL back (the variant
 * service was unreachable), and only if THAT also fails show the placeholder,
 * which is what this grid did before variants existed.
 */
const onTileImageError =
  (item: MenuItem) => (event: React.SyntheticEvent<HTMLImageElement>) => {
    const el = event.currentTarget;
    const original = item.image_url || "";
    if (original && el.dataset.variantFallback !== "1") {
      el.dataset.variantFallback = "1";
      el.src = original;
      return;
    }
    el.src = NO_DISPLAY_IMAGE;
  };

/* ------------------------------ REGULAR — eP ------------------------------- */

const RegularCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSoldOut",
})<{ isSoldOut?: boolean }>(({ isSoldOut }) => ({
  position: "relative",
  boxShadow: "rgba(0, 0, 0, 0.15) 0px 2px 8px",
  borderRadius: SHAPE.borderRadius,
  ...(isSoldOut
    ? { pointerEvents: "none", opacity: 0.5, textDecoration: "line-through" }
    : { "&:active": { transform: "scale(0.95)" } }),
}));

const RegularFrame = styled(ButtonBase)({
  position: "relative",
  width: "100%",
  paddingTop: "100%",
  borderRadius: SHAPE.borderRadius,
  overflow: "hidden",
});

const Scrim = styled("span")(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  backgroundColor: theme.palette.common.black,
  opacity: 0.2,
  transition: theme.transitions.create("opacity"),
}));

const ZoomLayer = styled("span")({
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  transition: "transform 0.5s ease",
  "&:hover": { transform: "scale(1.1)" },
});

const RegularImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center 40%",
  display: "block",
});

const BottomGradient = styled("span")({
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  pointerEvents: "none",
  background: BOTTOM_GRADIENT,
});

const PriceSlot = styled("span")(({ theme }) => ({
  position: "absolute",
  color: theme.palette.common.white,
  bottom: 5,
  left: 5,
}));

const SoldOutChip = styled(Chip)<{ component?: ElementType }>({
  fontWeight: FONT_WEIGHT_BOLD,
  borderRadius: SHAPE.borderRadius,
  height: "18px",
  fontSize: "14px",
  padding: 2,
});

const PopularChip = styled(Chip)({
  position: "absolute",
  top: -15,
  left: -15,
  zIndex: Z_INDEX.NONE,
  animation: `${popIn} 2s infinite`,
});

const RegularName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isSoldOut",
})<{ isSoldOut?: boolean }>(({ isSoldOut }) => ({
  ...clamp(2),
  textAlign: "left",
  marginTop: 5,
  ...(isSoldOut ? { textDecoration: "line-through" } : {}),
}));

const RegularDescription = styled(Typography)({ ...clamp(2), marginTop: 5 });

function RegularTile({
  item,
  itemQuantity,
  hidePrice,
  onClick,
  symbol,
  code,
}: ItemTileProps) {
  const soldOut = Boolean(item.sold_out);
  return (
    <>
      <RegularCard
        isSoldOut={soldOut}
        onPointerDown={() => preloadDetailImage(item)}
        onClick={() => onClick(item)}
      >
        <RegularFrame>
          <Scrim className="MuiImageBackdrop-root" />
          <ZoomLayer>
            <RegularImage
              src={imageOf(item) || NO_DISPLAY_IMAGE}
              alt={item.name}
              loading="lazy"
              onError={onTileImageError(item)}
            />
            <BottomGradient />
          </ZoomLayer>
          <PriceSlot>
            <Stack alignItems="flex-start" spacing={0.5}>
              <section>
                {soldOut ? (
                  <SoldOutChip label="Sold Out" color="error" size="small" />
                ) : hidePrice ? null : (
                  <ItemPrice item={item} symbol={symbol} code={code} color="secondary" />
                )}
              </section>
            </Stack>
          </PriceSlot>
        </RegularFrame>
        {item.featured && !soldOut && (
          <PopularChip label="Popular" size="small" color="primary" />
        )}
        <QuickAdd
          size="small"
          component="span"
          color={itemQuantity != null ? "primary" : "secondary"}
          disableFocusRipple
          disableRipple
        >
          {itemQuantity ?? <AddRoundedIcon />}
        </QuickAdd>
      </RegularCard>
      {!item.hide_name && (
        <RegularName
          variant="subtitle1"
          fontWeight="bold"
          color="text.primary"
          isSoldOut={soldOut}
        >
          {item.name}
        </RegularName>
      )}
      <RegularDescription variant="caption" color="text.secondary">
        {item.description}
      </RegularDescription>
    </>
  );
}

const MemoRegularTile = memo(RegularTile);

/* ------------------------------ CLASSIC — ex ------------------------------- */

const ClassicCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSoldOut",
})<{ isSoldOut?: boolean }>(({ theme, isSoldOut }) => ({
  position: "relative",
  maxWidth: "100%",
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  padding: 16,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: SHAPE.borderRadius,
  minHeight: 140,
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  ...(isSoldOut
    ? { pointerEvents: "none", opacity: 0.5 }
    : {
        "&:hover": {
          boxShadow: theme.shadows[4],
          transform: "translateY(-4px)",
        },
        "&:active": { transform: "scale(0.98)" },
      }),
}));

const ClassicBody = styled(Stack)({
  flex: 1,
  maxWidth: "100%",
  justifyContent: "space-between",
});

const ClassicName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isSoldOut",
})<{ isSoldOut?: boolean }>(({ isSoldOut }) => ({
  ...clamp(2),
  ...(isSoldOut ? { textDecoration: "line-through" } : {}),
}));

const ClassicDescription = styled(Typography)({ ...clamp(3) });

const ClassicImageBox = styled(Box)(({ theme }) => ({
  position: "relative",
  flexShrink: 0,
  width: 120,
  minWidth: 120,
  height: 120,
  borderRadius: SHAPE.borderRadius,
  overflow: "hidden",
  backgroundColor: theme.palette.grey[300],
}));

const ClassicImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  display: "block",
});

const ClassicPopularChip = styled(Chip)({
  position: "absolute",
  top: -15,
  left: -30,
  zIndex: Z_INDEX.NONE,
  animation: `${popIn} 2s infinite`,
});

function ClassicTile({ item, itemQuantity, onClick, symbol, code }: ItemTileProps) {
  const soldOut = Boolean(item.sold_out);
  const source = imageOf(item);
  return (
    <ClassicCard
      isSoldOut={soldOut}
      onPointerDown={() => preloadDetailImage(item)}
      onClick={() => onClick(item)}
    >
      <ClassicBody flex={1} spacing={0.5} justifyContent="space-evenly">
        {!item.hide_name && (
          <ClassicName variant="h6" fontWeight="bold" isSoldOut={soldOut}>
            {item.name}
          </ClassicName>
        )}
        <ClassicDescription variant="body2" color="text.secondary">
          {item.description}
        </ClassicDescription>
        <section>
          {soldOut ? (
            <SoldOutChip
              label="Sold Out"
              color="error"
              component="span"
              size="small"
            />
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              {/* CLASSIC's grid does not forward `hidePrice`, so the price is
                  ALWAYS shown here. Faithful, not an oversight. */}
              <ItemPrice item={item} symbol={symbol} code={code} />
              {item.featured && (
                <ClassicPopularChip label="Popular" size="small" color="primary" />
              )}
            </Stack>
          )}
        </section>
      </ClassicBody>
      <ClassicImageBox>
        {source && (
          <ClassicImage
            src={source}
            alt={item.name}
            loading="lazy"
            onError={onTileImageError(item)}
          />
        )}
        <BottomGradient />
        <QuickAdd
          size="small"
          component="span"
          color={itemQuantity != null ? "primary" : "secondary"}
        >
          {itemQuantity ?? <AddRoundedIcon />}
        </QuickAdd>
      </ClassicImageBox>
    </ClassicCard>
  );
}

const MemoClassicTile = memo(ClassicTile);

/* ------------------------------- SIMPLE — eV ------------------------------- */

const SimpleCard = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "isSoldOut",
})<{ isSoldOut?: boolean }>(({ theme, isSoldOut }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 20,
  backgroundColor: theme.palette.background.default,
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
  transition: "all 0.3s ease-in-out",
  border: "none",
  padding: PAGE_PADDING,
  height: "100%",
  ...(isSoldOut
    ? { pointerEvents: "none", opacity: 0.6 }
    : {
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
        },
        "&:active": { transform: "translateY(-2px)" },
      }),
  [theme.breakpoints.down("xs")]: {
    padding: 0,
    minHeight: 280,
    maxHeight: 280,
  },
}));

const SimpleFrame = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  paddingTop: "78%",
  overflow: "hidden",
  backgroundColor: theme.palette.action.disabledBackground,
  borderRadius: 20,
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    background:
      "linear-gradient(to top, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.05) 40%, transparent 70%)",
    pointerEvents: "none",
  },
  [theme.breakpoints.down("xs")]: { borderRadius: 0 },
}));

const SimpleImage = styled("img")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const SimpleSoldOutOverlay = styled("div")({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: Z_INDEX.BASE,
});

const SimpleSoldOutChip = styled(Chip)({
  fontSize: "0.75rem",
  fontWeight: FONT_WEIGHT_BOLD,
  borderRadius: "6px",
  height: "24px",
});

const SimplePopularChip = styled(Chip)({
  position: "absolute",
  top: 8,
  left: 8,
  zIndex: Z_INDEX.MID,
  fontWeight: 600,
  animation: `${popInSmall} 2s infinite`,
});

const SimplePriceSlot = styled("div")({
  position: "absolute",
  bottom: 8,
  left: 8,
  zIndex: Z_INDEX.NONE,
});

const SimpleContent = styled("div")(({ theme }) => ({
  padding: PAGE_PADDING,
  paddingBottom: 0,
  display: "flex",
  flexDirection: "column",
  gap: "15px",
  justifyContent: "space-between",
  alignItems: "center",
  textAlign: "center",
  width: "100%",
  flex: 1,
  [theme.breakpoints.down("xs")]: { paddingBottom: PAGE_PADDING },
}));

const SimpleName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isSoldOut",
})<{ isSoldOut?: boolean }>(({ theme, isSoldOut }) => ({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: 1.4,
  ...clamp(1),
  textAlign: "center",
  width: "100%",
  ...(isSoldOut ? { textDecoration: "line-through" } : {}),
  [theme.breakpoints.down("xs")]: {
    fontSize: "14px",
    WebkitLineClamp: 2,
    lineHeight: 1.2,
  },
}));

const SimpleDescription = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
  ...clamp(3),
  textAlign: "center",
  width: "100%",
  [theme.breakpoints.down("xs")]: { lineHeight: 1.2 },
}));

/** `e4` — the divider + "Add To Cart" chip, unique to SIMPLE and hidden <450px. */
const SimpleAction = styled(Stack)(({ theme }) => ({
  flex: 1,
  alignItems: "center",
  justifyContent: "flex-end",
  [theme.breakpoints.down("xs")]: { display: "none" },
}));

function SimpleTile({
  item,
  itemQuantity,
  hidePrice,
  onClick,
  symbol,
  code,
}: ItemTileProps) {
  const soldOut = Boolean(item.sold_out);
  return (
    <SimpleCard
      isSoldOut={soldOut}
      onPointerDown={() => preloadDetailImage(item)}
      onClick={() => onClick(item)}
    >
      <SimpleFrame>
        <SimpleImage
          src={imageOf(item) || NO_DISPLAY_IMAGE}
          alt={item.name}
          onError={onTileImageError(item)}
        />
        {soldOut && (
          <SimpleSoldOutOverlay>
            <SimpleSoldOutChip label="Sold Out" color="error" />
          </SimpleSoldOutOverlay>
        )}
        {item.featured && !soldOut && (
          <SimplePopularChip label="Popular" size="small" color="primary" />
        )}
        {!soldOut && !hidePrice && (
          <SimplePriceSlot>
            <ItemPrice item={item} symbol={symbol} code={code} color="secondary" />
          </SimplePriceSlot>
        )}
        <QuickAdd
          size="small"
          component="span"
          color={itemQuantity != null ? "primary" : "secondary"}
        >
          {itemQuantity ?? <AddRoundedIcon />}
        </QuickAdd>
      </SimpleFrame>
      <SimpleContent>
        {!item.hide_name && (
          <SimpleName variant="subtitle2" isSoldOut={soldOut}>
            {item.name}
          </SimpleName>
        )}
        <SimpleDescription variant="caption">{item.description}</SimpleDescription>
        <SimpleAction spacing={1.5}>
          <Divider sx={{ width: "100%" }} />
          <Chip label="Add To Cart" color="primary" />
        </SimpleAction>
      </SimpleContent>
    </SimpleCard>
  );
}

const MemoSimpleTile = memo(SimpleTile);

/* --------------------------------- the grid -------------------------------- */

/**
 * FIVE ITEMS PER ROW — the operator's own instruction, and a SIZING change
 * only. The three tiles above are untouched: same elements, same order, same
 * internal styling. What changed is how many of them fit across, and how the
 * row behaves when a category holds fewer items than a full row.
 *
 * WHY CONTAINER QUERIES AND NOT BREAKPOINTS.
 * The menu does NOT own the viewport. Under `CLASSIC*` (this tenant's design)
 * the basket is a PERMANENT drawer from `lg` up — 360px between `lg` and
 * `xxl`, then `30vw` capped at 500 — so at a 1280px window the catalog column
 * is ~890px wide, not 1280. A viewport-keyed ladder would put five columns in
 * a space that only holds four and overflow the page horizontally, which is
 * exactly what the brief rules out. `MenuScreen` marks the catalog column as a
 * containment context (`containerType: inline-size`, name `menu`) and the
 * ladder below measures THAT, so the count is right whether the basket is on
 * screen or not.
 *
 * The ladder, in container CONTENT width. ⚠ `@container` measures the content
 * box, so these numbers are the column width MINUS its 30px of page padding —
 * measured in the live page, not assumed:
 *
 *   < 420   2 columns   — a 360px phone lands here (330px of content)
 *   >= 420  3
 *   >= 640  4           — a 768px tablet lands here (738px of content)
 *   >= 820  5           — a 1280px desktop lands here (~875px of content,
 *                         i.e. 1280 minus the 360px permanent basket minus
 *                         the padding and the scrollbar)
 *   >= 1080 6
 *   >= 1320 7
 *   >= 1560 8           — wide ordering displays stay filled without making
 *                         five cards excessively wide.
 *
 * `repeat(N, minmax(0, 1fr))` rather than `auto-fill`: `auto-fill` would give
 * "about five", and the instruction is five. `minmax(0, …)` — not the implicit
 * `minmax(auto, …)` — is what lets a tile's clamped text shrink instead of
 * forcing the track wider than its share, which is the usual cause of a grid
 * that overflows its parent.
 *
 * The track count is capped by the number of items in the category. This keeps
 * a short category from reserving two or three invisible columns and leaving
 * a large blank area, while categories with a full row still retain the 2 → 5
 * responsive ladder.
 *
 * The `@supports` block is the fallback for a browser without container
 * queries (all three current engines have them): a plain viewport ladder,
 * pitched conservatively so it can never overflow next to the basket.
 */
const GRID_BASE = {
  display: "grid",
  width: "100%",
  gap: { xxs: 1.5, md: 2 },
} as const;

const tracks = (itemCount: number, maximum: number) =>
  `repeat(${Math.max(1, Math.min(itemCount, maximum))}, minmax(0, 1fr))`;

const cappedWidth = (itemCount: number, maximum: number, cardWidth: number) => {
  const count = Math.max(1, Math.min(itemCount, maximum));
  return `min(100%, ${count * cardWidth + Math.max(0, count - 1) * 16}px)`;
};

const tileGridSx = (itemCount: number) => ({
  ...GRID_BASE,
  gridTemplateColumns: tracks(itemCount, 2),
  maxWidth: cappedWidth(itemCount, 2, 200),
  justifyContent: "start",
  "@container menu (min-width: 420px)": {
    gridTemplateColumns: tracks(itemCount, 3),
    maxWidth: cappedWidth(itemCount, 3, 200),
  },
  "@container menu (min-width: 640px)": {
    gridTemplateColumns: tracks(itemCount, 4),
    maxWidth: cappedWidth(itemCount, 4, 200),
  },
  "@container menu (min-width: 820px)": {
    gridTemplateColumns: tracks(itemCount, 5),
    maxWidth: cappedWidth(itemCount, 5, 200),
  },
  "@container menu (min-width: 1080px)": {
    gridTemplateColumns: tracks(itemCount, 6),
    maxWidth: cappedWidth(itemCount, 6, 200),
  },
  "@container menu (min-width: 1320px)": {
    gridTemplateColumns: tracks(itemCount, 7),
    maxWidth: cappedWidth(itemCount, 7, 200),
  },
  "@container menu (min-width: 1560px)": {
    gridTemplateColumns: tracks(itemCount, 8),
    maxWidth: cappedWidth(itemCount, 8, 200),
  },
  "@supports not (container-type: inline-size)": {
    "@media (min-width: 450px)": {
      gridTemplateColumns: tracks(itemCount, 3),
    },
    "@media (min-width: 800px)": {
      gridTemplateColumns: tracks(itemCount, 4),
    },
    "@media (min-width: 1400px)": {
      gridTemplateColumns: tracks(itemCount, 5),
    },
    "@media (min-width: 1680px)": {
      gridTemplateColumns: tracks(itemCount, 7),
    },
  },
});

/**
 * CLASSIC is the WIDE horizontal row (image right, text left, 140px thumbnail),
 * so five of them across is meaningless — it stays one-up on a phone and
 * two-up once the column can carry two without crushing the 140px thumbnail.
 */
const rowGridSx = (itemCount: number) => ({
  ...GRID_BASE,
  gridTemplateColumns: tracks(itemCount, 1),
  "@container menu (min-width: 640px)": {
    gridTemplateColumns: tracks(itemCount, 2),
  },
  "@supports not (container-type: inline-size)": {
    "@media (min-width: 900px)": {
      gridTemplateColumns: tracks(itemCount, 2),
    },
  },
});

export type CategoryDisplayType = "REGULAR" | "CLASSIC" | "SIMPLE";

interface MenuItemGridProps {
  items: MenuItem[];
  displayType?: string;
  isModernDesign?: boolean;
  hidePrice?: boolean;
  quantities: Record<string, number | undefined>;
  onClick: (item: MenuItem) => void;
  symbol?: string;
  code?: string;
}

function MenuItemGrid({
  items,
  displayType,
  isModernDesign = false,
  hidePrice,
  quantities,
  onClick,
  symbol,
  code,
}: MenuItemGridProps) {
  const type = (String(displayType ?? "REGULAR").toUpperCase() ??
    "REGULAR") as CategoryDisplayType;

  const shared = (item: MenuItem): ItemTileProps => ({
    item,
    itemQuantity: quantities[item._id],
    isModernDesign,
    hidePrice,
    onClick,
    symbol,
    code,
  });

  if (type === "CLASSIC") {
    return (
      <Box sx={rowGridSx(items.length)}>
        {items.map((item) => (
          <MemoClassicTile key={item._id} {...shared(item)} />
        ))}
      </Box>
    );
  }

  if (type === "SIMPLE") {
    return (
      <Box sx={tileGridSx(items.length)}>
        {items.map((item) => (
          // `minWidth: 0` on the cell: SIMPLE's card is a `ButtonBase`, whose
          // `min-width: auto` default would otherwise let a long, unbroken item
          // name widen its own track and push the row past the column.
          <Box key={item._id} sx={{ minWidth: 0, display: "flex" }}>
            <MemoSimpleTile {...shared(item)} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={tileGridSx(items.length)}>
      {items.map((item) => (
        // REGULAR is not one element but three siblings (card, name,
        // description), so each item needs its own cell to stay a column.
        <Box key={item._id} sx={{ minWidth: 0 }}>
          <MemoRegularTile {...shared(item)} />
        </Box>
      ))}
    </Box>
  );
}

/** Cart changes create a new quantities map. Only the category containing the
 * changed item should rerender; every other category remains byte-for-byte
 * identical instead of rebuilding the entire (200+ item) catalog. */
const sameGridProps = (previous: MenuItemGridProps, next: MenuItemGridProps) => {
  if (
    previous.items !== next.items ||
    previous.displayType !== next.displayType ||
    previous.isModernDesign !== next.isModernDesign ||
    previous.hidePrice !== next.hidePrice ||
    previous.onClick !== next.onClick ||
    previous.symbol !== next.symbol ||
    previous.code !== next.code
  ) return false;

  return previous.items.every(
    (item) => previous.quantities[item._id] === next.quantities[item._id],
  );
};

export default memo(MenuItemGrid, sameGridProps);
